import { Injectable } from '@angular/core';
import { RequestConfigImplementation, REQUEST_METHODS, HTTPWrapperContext, XHR_STRATEGIES } from '@ptl/xhr';
import { DRUG_COVERAGE_STATUS_ENUM, IndividualDrugCoverageResponse, IndividualDrugItem } from 'src/app/shared/interfaces/drug-autocomplete-item.interface';
import { HttpHeaders } from '@angular/common/http';
import { BrandingConfigurationService } from '@ptl/brand';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { EffectiveDateStateStructure } from '../../../../shared/components/configs/interfaces/effective-date.state-structure.interface';
import { BehaviorSubject } from 'rxjs';
import { IndividualMarketProduct, Product } from '../../../interfaces/product.interface';
import { PlansStateStructure } from '../../../configs/interfaces/plansStateStructure.interface';
import { PlanCollection } from 'src/app/angular-elements/plan-selection/interfaces/plan-types-collection.interface';
import { MedicationStateStructure } from 'src/app/angular-elements/medical-information/medication-information/reducers/medication.reducer';
import { PlanAttributes, SummaryView } from '../../../interfaces/plan-attributes.interface';
@Injectable({
    providedIn: 'root'
})
export class IndividualDrugCoverageService {

    lookupComplete$: BehaviorSubject<Map<string, Map<string, DRUG_COVERAGE_STATUS_ENUM>>>
      = new BehaviorSubject<Map<string, Map<string, DRUG_COVERAGE_STATUS_ENUM>>>(null);
    activeStatus: 'LOADING' | 'NOT_ACTIVE' | 'COMPLETE' = 'NOT_ACTIVE';
    lookupMap: Map<string, Map<string, DRUG_COVERAGE_STATUS_ENUM>> = new Map();

    private _drugCoverageLookupResponse: IndividualDrugCoverageResponse = null;

    constructor(
        private httpClient: HTTPWrapperContext,
        private brandService: BrandingConfigurationService,
        private reducerUtilityService: ReducerUtilityService
    ) { }

    getPlanIdFromProduct(product: Product<PlanAttributes, SummaryView>): string {
      return product?.attributes?.PLAN_ID?.length ? product.attributes.PLAN_ID.split('_')[0] : null;
    }

    getHiosIdFromProduct(product: Product<PlanAttributes, SummaryView>): string {
      return product?.attributes?.HIOS_ID?.length ? product.attributes.HIOS_ID.split('-')[0] : null;
    }

    get drugCoverageLookupResponse(): IndividualDrugCoverageResponse {
      return JSON.parse(JSON.stringify(this._drugCoverageLookupResponse));
    }

    getCoverageDataForDrugAndPlanIds(drugIdPayloadString: string, planIdPlayloadString: string): Promise<IndividualDrugCoverageResponse> {
        const effectiveDateState: EffectiveDateStateStructure = this.reducerUtilityService.getReducerFromApplicationState('EffectiveDate');

        const payload = {
            year: effectiveDateState?.effectiveDateYear,
            searchtext: drugIdPayloadString,
            planids: planIdPlayloadString
        };

        const requestConfigImplementation = new RequestConfigImplementation({
            method: REQUEST_METHODS.POST,
            url: '/sxesvc/api/v1/druglookup/covered',
            headers: new HttpHeaders({
                ddToken: this.brandService.getCurrentBrand().ddToken_encoded()
            }),
            POST_OR_PUT_PAYLOAD: payload,
            strategyConfigs: { forceModernUrl: true }
        });
        this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_PRE_PASS);
        return this.httpClient.makeRequest<IndividualDrugCoverageResponse>(requestConfigImplementation);
    }

    /** Populate a map containing covered drug data by plan id */
    async triggerLookUpIfNotActive() {
      if (this.activeStatus === 'NOT_ACTIVE') {

        const drugIds = [];
        const planIds = [];

        const medState: MedicationStateStructure = this.reducerUtilityService.getState('MedicationInformation');

        if (medState?.individualMedications?.length) {

          this.activeStatus = 'LOADING';

          /** Drugs */
          const drugs: IndividualDrugItem[] = medState.individualMedications;

          // Drug Ids
          drugs.forEach(d => {
            drugIds.push(d.rxcui);
          });

          /** Plans */
          const planState: PlansStateStructure = this.reducerUtilityService.getComponentStates()['Plans'];
          if (planState.plansByPlanType) {

            // Plan Ids
            for (const planType in planState.plansByPlanType) {
              if (planState.plansByPlanType[planType]) {
                const x: PlanCollection<IndividualMarketProduct> = planState.plansByPlanType[planType];
                Object
                  .keys(x)
                  .map( ( i => {
                    let hiosId = (x[i] as IndividualMarketProduct).attributes.HIOS_ID;
                    if (hiosId) {
                      planIds.push(hiosId.split('-')[0]);
                    }
                  }));
              }
            }

          }

          if (drugIds.length && planIds.length) {

            const drugIdsAsString = drugIds.join(',');
            const planIdsAsString = planIds.join(',');

            this._drugCoverageLookupResponse
              = await this.getCoverageDataForDrugAndPlanIds(
                drugIdsAsString,
                planIdsAsString
              );

            /** Create a matrix housing plan/drug/status */
            this.drugCoverageLookupResponse.coverage.forEach(x => {

              /** Plan mapped for? No! */
              // plan_id is now equal to 'HIOS_ID' so the individual drug components use hios id to match plans now
              if (!this.lookupMap.get(x.plan_id)) {
                /** Create drug map */
                const drugCoveredMap = new Map();
                /** Populate it */
                drugCoveredMap.set(x.rxcui, x.coverage);
                /** Set it - into plan map */
                this.lookupMap.set(x.plan_id, drugCoveredMap);
              }

              /**
               * Plan mapped for? Yes!
               * Drug mapped for? No!
               */
              else if (!this.lookupMap.get(x.plan_id).get(x.rxcui)) {
                /** Populate it, Set it */
                this.lookupMap.get(x.plan_id).set(x.rxcui, x.coverage);
              }

            });

            this.activeStatus = 'COMPLETE';
            this.lookupComplete$.next(this.lookupMap);
          }
        }

      }
    }
}
